import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FaEye, FaSignOutAlt, FaSyncAlt, FaVolumeMute, FaVolumeUp } from 'react-icons/fa';
import { io } from 'socket.io-client';
import { applyAudioOutputDevice, MEETING_AUDIO_OUTPUT_KEY } from '../../utils/meetingAudio';
import {
  LiveMeetingMonitorMedia,
  sanitizeMonitorJoinQuery,
  validateMonitorJoinAck,
} from '../../utils/liveMeetingMonitor';
import { getMeetingSocketBaseUrl } from '../../utils/meetingSocketUrl';

const clean = (value) => String(value ?? '').trim();

export const applyMonitorTrackUpdate = (
  previous,
  {
    type,
    sessionId,
    producerId,
    mediaType,
    track,
  }
) => {
  const cleanSessionId = clean(sessionId);
  const cleanProducerId = clean(producerId);
  const cleanMediaType = clean(mediaType).toLowerCase();
  if (
    !cleanSessionId ||
    !cleanProducerId ||
    !['audio', 'video', 'screen'].includes(cleanMediaType)
  ) {
    return previous;
  }

  const existing = previous[cleanSessionId] || {
    tracks: {},
    producerIds: {},
  };
  if (type === 'remove') {
    if (existing.producerIds?.[cleanMediaType] !== cleanProducerId) {
      return previous;
    }
    return {
      ...previous,
      [cleanSessionId]: {
        ...existing,
        tracks: {
          ...(existing.tracks || {}),
          [cleanMediaType]: null,
        },
        producerIds: {
          ...(existing.producerIds || {}),
          [cleanMediaType]: '',
        },
      },
    };
  }

  return {
    ...previous,
    [cleanSessionId]: {
      ...existing,
      tracks: {
        ...(existing.tracks || {}),
        [cleanMediaType]: track || null,
      },
      producerIds: {
        ...(existing.producerIds || {}),
        [cleanMediaType]: cleanProducerId,
      },
    },
  };
};

const TrackElement = ({ track, kind, audioEnabled, onAutoplayBlocked }) => {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    element.srcObject = track ? new MediaStream([track]) : null;
    if (track && (kind !== 'audio' || audioEnabled)) {
      element.play?.().catch(() => {
        if (kind === 'audio') onAutoplayBlocked?.();
      });
    }
    return () => {
      if (element.srcObject) element.srcObject = null;
    };
  }, [audioEnabled, kind, onAutoplayBlocked, track]);

  useEffect(() => {
    if (kind !== 'audio' || !ref.current) return undefined;
    const applyOutput = ({ detail } = {}) => {
      const deviceId =
        typeof detail?.deviceId === 'string'
          ? clean(detail.deviceId)
          : clean(localStorage.getItem(MEETING_AUDIO_OUTPUT_KEY));
      applyAudioOutputDevice(ref.current, deviceId).catch(() => undefined);
    };
    applyOutput();
    window.addEventListener('meeting-output-device-change', applyOutput);
    return () =>
      window.removeEventListener('meeting-output-device-change', applyOutput);
  }, [kind]);

  if (!track) return null;
  if (kind === 'audio') {
    return (
      <audio
        ref={ref}
        autoPlay
        playsInline
        muted={!audioEnabled}
        data-monitor-media="remote-audio"
      />
    );
  }
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted
      className="h-full w-full bg-slate-950 object-contain"
      data-monitor-media={kind === 'screen' ? 'remote-screen' : 'remote-camera'}
    />
  );
};

const ObserverTile = ({
  participant,
  entry,
  audioEnabled,
  onAutoplayBlocked,
}) => {
  const tracks = entry?.tracks || {};
  const primaryTrack = tracks.screen || tracks.video;
  const isScreen = Boolean(tracks.screen);
  const displayName =
    clean(participant?.displayName) ||
    clean(participant?.username) ||
    clean(participant?.user?.username) ||
    clean(
      [participant?.firstName, participant?.lastName]
        .filter(Boolean)
        .join(' ')
    ) ||
    clean(participant?.role) ||
    'Meeting participant';

  return (
    <article className="relative flex min-h-[220px] overflow-hidden rounded-2xl border border-white/10 bg-slate-900 shadow-xl">
      {primaryTrack ? (
        <TrackElement
          track={primaryTrack}
          kind={isScreen ? 'screen' : 'video'}
          audioEnabled={audioEnabled}
          onAutoplayBlocked={onAutoplayBlocked}
        />
      ) : (
        <div className="flex h-full min-h-[220px] w-full items-center justify-center bg-gradient-to-br from-slate-800 to-slate-950">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-indigo-600 text-3xl font-semibold text-white">
            {displayName.slice(0, 1).toUpperCase()}
          </div>
        </div>
      )}
      <TrackElement
        track={tracks.audio}
        kind="audio"
        audioEnabled={audioEnabled}
        onAutoplayBlocked={onAutoplayBlocked}
      />
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/90 to-transparent px-4 pb-3 pt-10 text-white">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{displayName}</p>
          <p className="truncate text-[11px] text-slate-300">
            {clean(participant?.role) || 'Participant'}
            {isScreen ? ' · presenting screen' : ''}
          </p>
        </div>
        <div className="flex shrink-0 gap-1 text-[10px]">
          <span
            className={`rounded-full px-2 py-1 ${
              tracks.audio ? 'bg-emerald-500/80' : 'bg-slate-700/90'
            }`}
          >
            Mic {tracks.audio ? 'live' : 'off'}
          </span>
          <span
            className={`rounded-full px-2 py-1 ${
              primaryTrack ? 'bg-emerald-500/80' : 'bg-slate-700/90'
            }`}
          >
            {isScreen ? 'Screen' : `Cam ${primaryTrack ? 'live' : 'off'}`}
          </span>
        </div>
      </div>
    </article>
  );
};

const emitWithAck = (socket, event, payload, timeoutMs = 12_000) =>
  new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('The monitoring socket is not connected.'));
      return;
    }
    const timer = window.setTimeout(
      () => reject(new Error('The monitoring request timed out.')),
      timeoutMs
    );
    socket.emit(event, payload, (response = {}) => {
      window.clearTimeout(timer);
      if (response?.success === false) {
        const error = new Error(
          response.error || response.message || 'Monitoring request failed.'
        );
        error.code = response.code;
        reject(error);
        return;
      }
      resolve(response);
    });
  });

const LiveMeetingObserver = () => {
  const query = useMemo(
    () =>
      sanitizeMonitorJoinQuery(
        window.location.search,
        window.location.hash
      ),
    []
  );
  // Remove the one-time bearer before paint and before the passive
  // Socket.IO/media effect. Keeping parsing side-effect-free also survives
  // React StrictMode's development double render.
  useLayoutEffect(() => {
    const safePath =
      query.roomId
        ? `/monitor-join?roomId=${encodeURIComponent(query.roomId)}`
        : '/monitor-join';
    window.history.replaceState(null, document.title, safePath);
  }, [query.roomId]);
  const socketRef = useRef(null);
  const mediaRef = useRef(null);
  const leavingRef = useRef(false);
  const [status, setStatus] = useState(query.valid ? 'connecting' : 'invalid');
  const [errorMessage, setErrorMessage] = useState(
    query.valid ? '' : query.message
  );
  const [room, setRoom] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [observers, setObservers] = useState([]);
  const [trackState, setTrackState] = useState({});
  const [connectionState, setConnectionState] = useState('new');
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  const participantBySession = useMemo(
    () =>
      new Map(
        participants.map((participant) => [
          clean(participant?.sessionId),
          participant,
        ])
      ),
    [participants]
  );
  const visibleSessions = useMemo(() => {
    const values = new Set([
      ...participants.map((participant) => clean(participant?.sessionId)),
      ...Object.keys(trackState),
    ]);
    values.delete('');
    return [...values];
  }, [participants, trackState]);

  const leave = useCallback(async ({
    navigate = true,
    preserveStatus = false,
  } = {}) => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    if (!preserveStatus) setStatus('leaving');
    const socket = socketRef.current;
    const media = mediaRef.current;
    mediaRef.current = null;
    try {
      // Keep observer socket presence valid until its receive-only transports
      // and consumers have been deleted.
      await media?.close?.();
      if (socket?.connected) {
        await emitWithAck(socket, 'monitor:leave', {}, 3_000).catch(
          () => undefined
        );
      }
    } finally {
      socket?.disconnect();
      socketRef.current = null;
      if (navigate) window.location.assign('/');
    }
  }, []);

  useEffect(() => {
    if (!query.valid) return undefined;
    let cancelled = false;
    let heartbeatTimer = null;
    let producerSyncTimer = null;
    const socket = io(getMeetingSocketBaseUrl(), {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      tryAllTransports: true,
      withCredentials: false,
      reconnection: false,
      timeout: 20_000,
      // Do not copy a normal participant JWT into the observer socket.
      auth: { monitorOnly: true },
    });
    socketRef.current = socket;

    const fail = (error) => {
      if (cancelled || leavingRef.current) return;
      setStatus('error');
      setErrorMessage(
        error?.message ||
          'The monitoring session could not be opened. Return to the portal and try again.'
      );
      void leave({ navigate: false, preserveStatus: true });
    };
    const updateObservers = (payload = {}) => {
      if (clean(payload.roomId) !== query.roomId) return;
      if (Array.isArray(payload.observers)) {
        setObservers(payload.observers);
      }
    };

    socket.on('meeting-observers-updated', updateObservers);
    socket.on('meeting-observer-joined', ({ roomId, observer } = {}) => {
      if (clean(roomId) !== query.roomId || !observer) return;
      setObservers((previous) => {
        const key = clean(observer.auditId || observer.userId);
        return [
          ...previous.filter(
            (item) => clean(item.auditId || item.userId) !== key
          ),
          observer,
        ];
      });
    });
    socket.on('meeting-observer-left', ({ roomId, observer } = {}) => {
      if (clean(roomId) !== query.roomId || !observer) return;
      const key = clean(observer.auditId || observer.userId);
      setObservers((previous) =>
        previous.filter(
          (item) => clean(item.auditId || item.userId) !== key
        )
      );
    });
    socket.on('newParticipant', (participant) => {
      const sessionId = clean(participant?.sessionId);
      if (!sessionId) return;
      setParticipants((previous) => [
        ...previous.filter(
          (item) => clean(item?.sessionId) !== sessionId
        ),
        participant,
      ]);
    });
    socket.on('participantUpdated', ({ participantId, updates } = {}) => {
      const key = clean(participantId);
      if (!key) return;
      setParticipants((previous) =>
        previous.map((participant) =>
          [participant?.sessionId, participant?.userId].some(
            (value) => clean(value) === key
          )
            ? { ...participant, ...(updates || {}) }
            : participant
        )
      );
    });
    const removeParticipant = ({ sessionId } = {}) => {
      const key = clean(sessionId);
      if (!key) return;
      setParticipants((previous) =>
        previous.filter((participant) => clean(participant?.sessionId) !== key)
      );
    };
    socket.on('participant-left', removeParticipant);
    socket.on('participantLeft', removeParticipant);
    socket.on('participant-disconnected', ({ sessionId, disconnectedAt } = {}) => {
      const key = clean(sessionId);
      if (!key) return;
      setParticipants((previous) =>
        previous.map((participant) =>
          clean(participant?.sessionId) === key
            ? {
                ...participant,
                disconnected: true,
                disconnectedAt: disconnectedAt || new Date().toISOString(),
              }
            : participant
        )
      );
    });
    socket.on('meeting-ended', () => {
      setStatus('ended');
      setErrorMessage('This meeting has ended. Live monitoring is closed.');
      void leave({ navigate: false, preserveStatus: true });
    });
    socket.on('monitor:action-denied', (payload = {}) => {
      fail(new Error(payload.message || 'Observer mode is read-only.'));
    });

    socket.once('connect', async () => {
      try {
        const joined = validateMonitorJoinAck(
          await emitWithAck(socket, 'monitor:join', {
            roomId: query.roomId,
            monitorToken: query.monitorToken,
          })
        );
        if (cancelled) return;
        setRoom(joined.room || null);
        setParticipants(
          Array.isArray(joined.room?.participants)
            ? joined.room.participants
            : []
        );
        setObservers(Array.isArray(joined.observers) ? joined.observers : []);

        // Remove the one-time secret from browser history as soon as the
        // socket consumes it. It is never placed in storage or MediaSoup URLs.
        window.history.replaceState(
          null,
          document.title,
          `/monitor-join?roomId=${encodeURIComponent(query.roomId)}`
        );

        const media = new LiveMeetingMonitorMedia({
          roomId: query.roomId,
          monitorSessionId: joined.monitorSessionId,
          onTrack: (track) =>
            setTrackState((previous) =>
              applyMonitorTrackUpdate(previous, { type: 'add', ...track })
            ),
          onTrackRemoved: (track) =>
            setTrackState((previous) =>
              applyMonitorTrackUpdate(previous, { type: 'remove', ...track })
            ),
          onConnectionState: setConnectionState,
          onError: (error) => {
            if ([401, 403, 409, 410].includes(error?.response?.status)) {
              fail(error);
            }
          },
        });
        mediaRef.current = media;

        socket.on('newProducer', (producer) => {
          void media.consumeProducer(producer).catch((error) => {
            if (![404, 410].includes(error?.response?.status)) fail(error);
          });
        });
        socket.on('producerClosed', ({ producerId } = {}) => {
          void media.removeProducer(producerId, {
            notifyServer: false,
          });
        });

        await media.initialize();
        if (cancelled) return;
        setStatus('live');
        heartbeatTimer = window.setInterval(() => {
          if (!socket.connected) return;
          socket.emit('monitor:heartbeat', {}, (response = {}) => {
            if (response.success === false) {
              fail(
                new Error(
                  response.error ||
                    'The visible monitoring session is no longer active.'
                )
              );
            }
          });
        }, 20_000);
        producerSyncTimer = window.setInterval(() => {
          if (document.visibilityState === 'hidden') return;
          void media.syncProducers().catch(fail);
        }, 30_000);
      } catch (error) {
        fail(error);
      }
    });
    socket.on('connect_error', fail);
    socket.on('disconnect', (reason) => {
      if (cancelled || leavingRef.current) return;
      fail(
        new Error(
          reason === 'io server disconnect'
            ? 'The server closed this monitoring session.'
            : 'The monitoring connection was lost. Reopen it from the Admin/Staff portal.'
        )
      );
    });

    const onBeforeUnload = () => {
      if (socket.connected) socket.emit('monitor:leave', {});
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      cancelled = true;
      window.clearInterval(heartbeatTimer);
      window.clearInterval(producerSyncTimer);
      window.removeEventListener('beforeunload', onBeforeUnload);
      const media = mediaRef.current;
      mediaRef.current = null;
      void media?.close?.().finally(() => {
        if (socket.connected) socket.emit('monitor:leave', {});
        socket.disconnect();
      });
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [leave, query]);

  const enableAudio = useCallback(() => {
    setAudioEnabled(true);
    setAutoplayBlocked(false);
    document
      .querySelectorAll('[data-monitor-media="remote-audio"]')
      .forEach((element) => element.play?.().catch(() => undefined));
  }, []);

  if (!query.valid || ['error', 'ended'].includes(status)) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-950 px-4 text-white">
        <section className="w-full max-w-lg rounded-2xl border border-rose-400/25 bg-slate-900 p-7 text-center shadow-2xl">
          <FaEye className="mx-auto mb-4 text-4xl text-rose-300" />
          <h1 className="text-xl font-semibold">Live monitoring unavailable</h1>
          <p className="mt-3 text-sm leading-6 text-slate-300">
            {errorMessage ||
              'Return to the Admin/Staff portal and create a new monitoring link.'}
          </p>
          <button
            type="button"
            onClick={() => window.location.assign('/')}
            className="mt-6 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold hover:bg-indigo-500"
          >
            Return to portal
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen flex-col bg-slate-950 text-white">
      <header className="sticky top-0 z-40 border-b border-amber-300/30 bg-amber-950/95 px-4 py-3 shadow-xl backdrop-blur">
        <div className="mx-auto flex max-w-[1800px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-400 text-amber-950">
              <FaEye />
            </span>
            <div className="min-w-0">
              <h1 className="truncate text-base font-bold text-amber-100 sm:text-lg">
                Admin/Staff observer · visible to all participants
              </h1>
              <p className="text-xs leading-5 text-amber-200/85">
                Read-only live monitoring. Your microphone and camera are never
                requested or published.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={audioEnabled ? () => setAudioEnabled(false) : enableAudio}
              className="inline-flex items-center gap-2 rounded-lg border border-amber-200/30 bg-black/20 px-3 py-2 text-xs font-semibold hover:bg-black/35"
            >
              {audioEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
              {audioEnabled ? 'Mute live audio' : 'Enable live audio'}
            </button>
            <button
              type="button"
              onClick={() => void leave()}
              className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-2 text-xs font-semibold hover:bg-rose-500"
            >
              <FaSignOutAlt />
              Leave monitor
            </button>
          </div>
        </div>
      </header>

      <section className="mx-auto flex w-full max-w-[1800px] flex-1 flex-col px-4 py-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-slate-900 px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold">
              {clean(room?.title) || clean(room?.className) || query.roomId}
            </h2>
            <p className="text-xs text-slate-400">
              {visibleSessions.length} live participant
              {visibleSessions.length === 1 ? '' : 's'} · {observers.length}{' '}
              visible observer{observers.length === 1 ? '' : 's'}
            </p>
          </div>
          <div
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${
              status === 'live' && connectionState === 'connected'
                ? 'bg-emerald-500/15 text-emerald-300'
                : 'bg-amber-500/15 text-amber-200'
            }`}
          >
            {status === 'live' && connectionState === 'connected' ? (
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
            ) : (
              <FaSyncAlt className="animate-spin" />
            )}
            {status === 'live'
              ? connectionState === 'connected'
                ? 'Live · receive-only'
                : 'Restoring live media'
              : status === 'leaving'
                ? 'Closing monitor'
                : 'Connecting securely'}
          </div>
        </div>

        {(autoplayBlocked || (!audioEnabled && status === 'live')) && (
          <button
            type="button"
            onClick={enableAudio}
            className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl border border-indigo-300/25 bg-indigo-500/15 px-4 py-3 text-sm font-semibold text-indigo-100 hover:bg-indigo-500/25"
          >
            <FaVolumeUp />
            Tap to hear live meeting audio
          </button>
        )}

        {visibleSessions.length ? (
          <div className="grid flex-1 auto-rows-fr grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {visibleSessions.map((sessionId) => (
              <ObserverTile
                key={sessionId}
                participant={participantBySession.get(sessionId)}
                entry={trackState[sessionId]}
                audioEnabled={audioEnabled}
                onAutoplayBlocked={() => setAutoplayBlocked(true)}
              />
            ))}
          </div>
        ) : (
          <div className="flex min-h-[360px] flex-1 items-center justify-center rounded-2xl border border-dashed border-white/15 bg-slate-900/60 text-center">
            <div className="max-w-md px-6">
              <FaEye className="mx-auto mb-4 text-4xl text-slate-500" />
              <h3 className="text-lg font-semibold">Waiting for live media</h3>
              <p className="mt-2 text-sm leading-6 text-slate-400">
                This observer never turns on a local camera or microphone. Live
                participant cameras, audio and screen shares appear here.
              </p>
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default LiveMeetingObserver;
